import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Lock, RefreshCw, LogOut } from "lucide-react";
import { BLOGS } from "../lib/blogs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SwitchboardStats() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem("sb_admin_token") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const authed = !!data;

  const loadStats = useCallback(async (t) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/insights/stats`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) {
        setToken(""); sessionStorage.removeItem("sb_admin_token"); setData(null);
        setLoading(false); return;
      }
      setData(await res.json());
    } catch (e) {
      setError("Could not load stats. Please try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (token) loadStats(token); }, [token, loadStats]);

  const login = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) { setError("Invalid email or password."); setLoading(false); return; }
      const j = await res.json();
      sessionStorage.setItem("sb_admin_token", j.token);
      setToken(j.token);
      setPassword("");
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const logout = () => { sessionStorage.removeItem("sb_admin_token"); setToken(""); setData(null); };

  const viewsBySlug = {};
  (data?.blogs || []).forEach((b) => { viewsBySlug[b.slug] = b.views; });
  const rows = BLOGS
    .map((b) => ({ number: b.number, title: b.title, slug: b.slug, views: viewsBySlug[b.slug] || 0 }))
    .sort((a, b) => b.views - a.views);
  const maxViews = Math.max(1, ...rows.map((r) => r.views));

  return (
    <div className="min-h-screen bg-[#161A15] pb-24 pt-36 font-body text-white md:pt-44">
      <Helmet>
        <title>Switchboard Reading Stats | SWITCH</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mx-auto max-w-[1000px] px-6 md:px-12">
        <span className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">Internal</span>
        <h1 className="font-display mt-4 text-4xl uppercase leading-[0.95] tracking-tight md:text-6xl">Switchboard reading stats</h1>
        <p className="font-body mt-5 max-w-xl text-base font-normal text-white/70">See which articles are landing with readers. Restricted to the SWITCH team.</p>

        {!authed ? (
          <form onSubmit={login} data-testid="stats-login" className="mt-12 max-w-sm space-y-4">
            <label className="font-body flex items-center gap-2 text-sm font-normal text-white/60">
              <Lock size={14} /> Team sign in
            </label>
            <input
              type="email" required data-testid="stats-email-input" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="font-body w-full border border-white/25 bg-transparent px-4 py-3 text-base font-normal text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#5f8f10]"
            />
            <input
              type="password" required data-testid="stats-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="font-body w-full border border-white/25 bg-transparent px-4 py-3 text-base font-normal text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#5f8f10]"
            />
            <button type="submit" data-testid="stats-signin" className="font-body w-full border border-[#5f8f10] bg-[#5f8f10] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#161A15] transition-colors hover:border-[#4a700c] hover:bg-[#4a700c]">
              {loading ? "Signing in" : "Sign in"}
            </button>
            {error && <p data-testid="stats-error" className="font-body text-sm font-semibold text-red-400">{error}</p>}
          </form>
        ) : (
          <div className="mt-12" data-testid="stats-table">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-white/60">
                Total reads: <span className="text-[#5f8f10]">{data?.total_views ?? 0}</span>
              </p>
              <div className="flex items-center gap-5">
                <button type="button" onClick={() => loadStats(token)} className="group inline-flex items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors hover:text-[#5f8f10]">
                  <RefreshCw size={14} /> Refresh
                </button>
                <button type="button" onClick={logout} data-testid="stats-logout" className="group inline-flex items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors hover:text-[#5f8f10]">
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </div>
            <div className="mt-6 border-t border-white/12">
              {rows.map((r) => (
                <div key={r.slug} className="grid grid-cols-12 items-center gap-4 border-b border-white/12 py-5">
                  <span className="font-display col-span-1 text-2xl leading-none text-[#5f8f10]">{r.number}</span>
                  <div className="col-span-7 md:col-span-8">
                    <Link to={`/insights/${r.slug}`} className="font-body text-base font-normal text-white transition-colors hover:text-[#5f8f10]">{r.title}</Link>
                    <div className="mt-2 h-1.5 w-full bg-white/10">
                      <div className="h-full bg-[#5f8f10]" style={{ width: `${(r.views / maxViews) * 100}%` }} />
                    </div>
                  </div>
                  <span className="font-display col-span-4 col-start-9 text-right text-2xl leading-none text-white md:col-span-3">{r.views}<span className="font-body ml-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/40">reads</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
